import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import {
  Box,
  makeStyles,
  Button,
  Card,
  CardActions,
  Checkbox,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { API_URL } from "src/constants";
import * as actions from "src/actions/auth";

import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: "100%",
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
  courseCard: {
    height: "100%",
  },
}));

const CreateCourseForm = ({ className, ...rest }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [courseCategory, setCourseCategory] = useState("none");

  const [fileUpload, setFile] = useState(0);

  const fileChangedHandler = (event) => {
    const file = event.target.files[0];
    setFile(file);
  };

  const submitHandler = (values) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("description", values.description);
    formData.append("category", courseCategory);
    formData.append("requirements", values.requirements);
    formData.append("who_its_for", values.whoItsFor);
    formData.append("course_outcome", values.outcomes);
    formData.append("price", values.price);
    formData.append("published", values.published === false ? "0" : "1");
    formData.append("organization", process.env.REACT_APP_ORGANIZATION);
    // TODO: show alert if no file is selected
    if (fileUpload === 0) {
      formData.append("image", ""); // or just return
    } else {
      formData.append("image", fileUpload, fileUpload.name);
    }

    const token = localStorage.getItem("token");
    const headers = {
      "content-type": "application/json",
      Authorization: `Token ${token}`,
    };
    API_URL({
      method: "post",
      url: `courses/courses/`,
      data: formData,
      headers: headers,
    })
      .then((response) => {
        actions.postBreadcrumb(
          "post",
          `courses/courses/`,
          `created course ${response.data.slug}`
        );
        navigate(`/app/edit_course/${response.data.slug}`);
      })
      .catch((error) => {
        console.log("Error occured: ", error);
      });
  };

  return (
    <Grid>
      <Grid>
        <Card className={clsx(classes.root, className)} {...rest}>
          <CardActions>
            <Grid
              container
              alignContent="center"
              alignItems="center"
              direction="column"
            >
              <Grid item xs>
                <Typography color="textSecondary" variant="h5">
                  Add a course banner.
                </Typography>
                <Grid>
                  <Typography color="textSecondary" variant="caption">
                    Images of 1 by 1 aspect ratio work best.
                  </Typography>
                </Grid>
                <Grid>
                  <Typography color="textSecondary" variant="caption">
                    For example 320x320 pixels.
                  </Typography>
                </Grid>
                <Grid>
                  <input type="file" onChange={fileChangedHandler} />
                </Grid>
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Grid>
      <Grid>
        <Formik
          initialValues={{
            name: "",
            description: "",
            requirements: "",
            whoItsFor: "",
            outcomes: "",
            price: "0",
            published: false,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .max(255)
              .required("A course must have a name"),
            description: Yup.string()
              .max(255)
              .required("A course needs a description"),
          })}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form onSubmit={handleSubmit}>
              <TextField
                error={Boolean(touched.name && errors.name)}
                fullWidth
                helperText={touched.name && errors.name}
                label="Course Name"
                margin="normal"
                name="name"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                variant="outlined"
              />
              <TextField
                error={Boolean(touched.description && errors.description)}
                fullWidth
                helperText={touched.description && errors.description}
                label="Course Description"
                margin="normal"
                name="description"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.description}
                variant="outlined"
                multiline
              />
              <Typography
                variant="caption"
                color="textSecondary"
                gutterBottom={false}
              >
                List any previous requirements for this course:
              </Typography>
              <TextField
                error={Boolean(touched.requirements && errors.requirements)}
                fullWidth
                helperText={touched.requirements && errors.requirements}
                label="Optional: Course Prerequisites"
                margin="normal"
                name="requirements"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.requirements}
                variant="outlined"
                multiline
              />
              <TextField
                error={Boolean(touched.whoItsFor && errors.whoItsFor)}
                fullWidth
                helperText={touched.whoItsFor && errors.whoItsFor}
                label="Optional: Who is this course for?"
                margin="normal"
                name="whoItsFor"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.whoItsFor}
                variant="outlined"
                multiline
              />
              <Typography
                variant="caption"
                color="textSecondary"
                gutterBottom={false}
              >
                List course benefits to the user:
              </Typography>
              <TextField
                error={Boolean(touched.outcomes && errors.outcomes)}
                fullWidth
                helperText={touched.outcomes && errors.outcomes}
                label="Optional: What will the user learn?"
                margin="normal"
                name="outcomes"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.outcomes}
                variant="outlined"
                multiline
              />
              <Grid>
                <FormControl className={classes.formControl} fullWidth>
                  <InputLabel shrink id="simple-select-placeholder-label-label">
                    Choose the course category
                  </InputLabel>
                  <Select
                    labelId="simple-select-placeholder-label-label"
                    id="simple-select-placeholder-label"
                    value={courseCategory}
                    onChange={(event) => {
                      setCourseCategory(event.target.value);
                    }}
                    displayEmpty
                    className={classes.selectEmpty}
                  >
                    <MenuItem value={"none"}>None</MenuItem>
                    <MenuItem value={"computer-science"}>
                      Computer Science
                    </MenuItem>
                    <MenuItem value={"business"}>Business</MenuItem>
                    <MenuItem value={"personal-development"}>
                      Personal Development
                    </MenuItem>
                    <MenuItem value={"language-learning"}>
                      Language Learning
                    </MenuItem>
                    <MenuItem value={"information-technology"}>
                      Information Technology
                    </MenuItem>
                    <MenuItem value={"health"}>Health</MenuItem>
                    <MenuItem value={"math-and-logic"}>Math and Logic</MenuItem>
                    <MenuItem value={"engineering"}>Engineering</MenuItem>
                    <MenuItem value={"social-sciences"}>
                      Social Sciences
                    </MenuItem>
                    <MenuItem value={"arts-and-humanities"}>
                      Arts and Humanities
                    </MenuItem>
                    <MenuItem value={"design"}>Design</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Typography variant="caption" color="textSecondary">
                Please know that paid courses are not activated yet. However,
                you may include your intended price for the course as this will
                help us to create a pricing model that works best for the
                platform. Thanks for understanding.
              </Typography>
              <TextField
                error={Boolean(touched.price && errors.price)}
                fullWidth
                helperText={touched.price && errors.price}
                label="Price"
                margin="normal"
                name="price"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.price}
                variant="outlined"
              />
              <Box alignItems="center" display="flex" ml={-1}>
                <Checkbox
                  checked={values.published}
                  name="published"
                  onChange={handleChange}
                />
                <Typography color="textSecondary" variant="body1">
                  Publish
                </Typography>
              </Box>
              {Boolean(touched.published && errors.published) && (
                <FormHelperText error>{errors.published}</FormHelperText>
              )}
              <Box my={3}>
                <Button
                  color="primary"
                  disabled={false} // TODO: make programatic
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Create
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default CreateCourseForm;
